// What an answer earned, worked out on the phone from the reveal, for the formats that are not "pick the one right
// option". The server's own record (GET /me) always wins once it arrives; these rules only let the answer view speak at
// once, and they follow the contract (shared/types.ts, the conventions above NumberSpec) and shared/scoring:
//   number   full within ± tolerance, then partial while inside tolerance + span (and still worth a point), nothing beyond
//   order    the exact order is full; one swap of two neighbouring steps is partial; anything else none
//   multi    'all': every right option and nothing else is full; more right picks than wrong ones is partial
//            'rarity': one pick, right when it is any right option; rarer right picks score more, worked out at the close
//   hotspot  one tap, right when it is any right region
import type { AnswerRequest, NumberSpec, PublicItem, Reveal } from '@shared/types';
import { sharePct } from './layout';

export type Credit = 'full' | 'partial' | 'none';
export const CREDITS: readonly Credit[] = ['full', 'partial', 'none'];
export const isCredit = (s: unknown): s is Credit => s === 'full' || s === 'partial' || s === 'none';

type Payload = Omit<AnswerRequest, 'index'>;
type Q = Pick<PublicItem, 'format' | 'multi' | 'number' | 'maxPoints' | 'multiplier'>;

/**
 * How a format takes its answer: 'pick' = one option, right when it is the key (mcq, tf, gmcq, spot, clues);
 * 'pick-any' = one option or region, right when it is any of the right ones (hotspot, multi rarity);
 * 'credit' = a whole answer that lands in a credit bucket (number, order, multi 'all').
 */
export type AnswerKind = 'pick' | 'pick-any' | 'credit';
export function answerKind(q: Pick<PublicItem, 'format' | 'multi'>): AnswerKind {
  switch (q.format) {
    case 'number': case 'order': return 'credit';
    case 'multi': return q.multi?.mode === 'rarity' ? 'pick-any' : 'credit';
    case 'hotspot': return 'pick-any';
    default: return 'pick';
  }
}

/**
 * number: full within ± tolerance; beyond it the base falls linearly to nothing across the span, and the answer is
 * partial while that still rounds to at least a point of the base (as the server rounds it), else none.
 */
export function numberCredit(value: number, answer: number, spec: Pick<NumberSpec, 'tolerance' | 'span'>, base = 500): Credit {
  if (!Number.isFinite(value) || !Number.isFinite(answer)) return 'none';
  const eps = 1e-9 * Math.max(1, Math.abs(answer));
  const d = Math.abs(value - answer);
  const tol = Math.max(0, Number(spec.tolerance) || 0);
  if (d <= tol + eps) return 'full';
  const span = Math.max(0, Number(spec.span) || 0);
  if (!(span > 0)) return 'none';
  const fraction = 1 - (d - tol) / span;
  return fraction > eps && Math.round(base * fraction) > 0 ? 'partial' : 'none';
}

export function orderCredit(order: readonly string[], correct: readonly string[]): Credit {
  if (!correct.length || order.length !== correct.length) return 'none';
  const diff = order.flatMap((id, i) => (id === correct[i] ? [] : [i]));
  if (diff.length === 0) return 'full';
  const [a, b] = diff;
  return diff.length === 2 && b === a + 1 && order[a] === correct[b] && order[b] === correct[a] ? 'partial' : 'none';
}

/** multi 'all': every right option and nothing else is full; otherwise the base × (right − wrong) ÷ number right, partial while that rounds to a point. */
export function multiCredit(picked: readonly string[], correct: readonly string[], base = 500): Credit {
  const right = new Set(correct);
  const mine = new Set(picked);
  let hits = 0; let misses = 0;
  for (const id of mine) { if (right.has(id)) hits++; else misses++; }
  if (right.size > 0 && hits === right.size && misses === 0) return 'full';
  const fraction = right.size > 0 ? Math.max(0, (hits - misses) / right.size) : 0;
  return Math.round(base * fraction) > 0 ? 'partial' : 'none';
}

/** Every right option or region (hotspot, multi), else the one key. */
export function rightIds(r: Pick<Reveal, 'correctId' | 'correctIds'>): string[] {
  return Array.isArray(r.correctIds) && r.correctIds.length ? r.correctIds : [r.correctId];
}
export const isRightPick = (id: string | undefined, r: Pick<Reveal, 'correctId' | 'correctIds'>): boolean => Boolean(id) && rightIds(r).includes(id!);

/** What a whole answer earned, from the reveal; undefined when the reveal or the answer cannot say. */
export function creditFor(q: Q, r: Reveal, a: Payload | undefined): Credit | undefined {
  if (!a) return undefined;
  const kind = answerKind(q);
  if (kind === 'pick') return a.optionId ? (a.optionId === r.correctId ? 'full' : 'none') : undefined;
  if (kind === 'pick-any') return a.optionId ? (isRightPick(a.optionId, r) ? 'full' : 'none') : undefined;
  const base = Math.round((typeof q.maxPoints === 'number' && q.maxPoints > 0 ? q.maxPoints : 1000) / 2);
  if (q.format === 'number' && typeof a.value === 'number' && typeof r.numberAnswer === 'number') {
    return numberCredit(a.value, r.numberAnswer, q.number ?? { tolerance: 0, span: 0 }, base);
  }
  if (q.format === 'order' && Array.isArray(a.order) && Array.isArray(r.correctOrder)) return orderCredit(a.order, r.correctOrder);
  if (q.format === 'multi' && Array.isArray(a.optionIds) && Array.isArray(r.correctIds)) return multiCredit(a.optionIds, r.correctIds, base);
  // The server keeps the bucket as the answer's option id; a record read back from /me may carry only that.
  return isCredit(a.optionId) ? a.optionId : undefined;
}

/** Share of the answering players who got it fully right, 0–100, or undefined when nobody answered (or the reveal cannot say). */
export function roomRightPct(q: Pick<PublicItem, 'format' | 'multi'>, r: Reveal): number | undefined {
  if (!(r.answered > 0)) return undefined;
  const kind = answerKind(q);
  if (kind === 'pick') return (100 * (r.counts[r.correctId] ?? 0)) / r.answered;
  if (kind === 'pick-any') return (100 * rightIds(r).reduce((n, id) => n + (r.counts[id] ?? 0), 0)) / r.answered;
  if (r.credit) return (100 * r.credit.full) / Math.max(r.answered, r.credit.full + r.credit.partial + r.credit.none);
  return typeof r.counts.full === 'number' ? (100 * r.counts.full) / r.answered : undefined;
}

/** The room's share for one option or region, as a whole percentage; rarity shares come from the server's own figure. */
export function optionPct(q: Pick<PublicItem, 'format' | 'multi'>, r: Reveal, id: string): number {
  if (q.format === 'multi' && q.multi?.mode === 'rarity' && r.rarity && typeof r.rarity[id] === 'number') return Math.round(r.rarity[id]);
  return sharePct(r, id);
}

/**
 * Points for a rarity pick, as the server works them out at the close: a right pick scores max(10, 100 − its share)
 * per cent of the item's maximum, times the multiplier; a wrong pick scores nothing. Undefined until the reveal
 * carries the shares.
 */
export function rarityPoints(q: Q, r: Reveal, optionId: string | undefined): number | undefined {
  if (!optionId) return undefined;
  if (!isRightPick(optionId, r)) return 0;
  if (!r.rarity || typeof r.rarity[optionId] !== 'number') return undefined;
  const max = typeof q.maxPoints === 'number' && q.maxPoints > 0 ? q.maxPoints : 1000;
  const m = typeof q.multiplier === 'number' && Number.isFinite(q.multiplier) && q.multiplier > 0 ? q.multiplier : 1;
  return Math.round((Math.max(10, 100 - r.rarity[optionId]) / 100) * max * m);
}

/** Whole percentages for the credit split that always add up to 100 (largest remainder); zeros when nobody answered. */
export function creditShares(c: { full: number; partial: number; none: number }): Record<Credit, number> {
  const raw = CREDITS.map((k) => Math.max(0, Number(c[k]) || 0));
  const total = raw.reduce((a, b) => a + b, 0);
  if (!total) return { full: 0, partial: 0, none: 0 };
  const exact = raw.map((v) => (100 * v) / total);
  const out = exact.map(Math.floor);
  let left = 100 - out.reduce((a, b) => a + b, 0);
  const order = exact.map((v, i) => ({ i, rem: v - Math.floor(v) })).sort((x, y) => y.rem - x.rem);
  for (const { i } of order) { if (left <= 0) break; out[i]++; left--; }
  return { full: out[0], partial: out[1], none: out[2] };
}
